import ComplianceAnswerHistory from "@/components/probaworkflows_base_components/ComplianceAnswerHistory/ComplianceAnswerHistory.vue";
import FilePreviewComponent from "@/components/probaworkflows_base_components/FilePreviewComponent/FilePreviewComponent.vue";
import {
  GET_ENTITY_COMPLIANCE_OVERVIEW_URL,
  get_headers,
  GET_RULE_QUESTIONS_ANSWERS_URL,
  SUBMIT_COMPLIANCE_ANSWER_URL,
  SYNC_APPLICATION_ANSWERS_TO_COMPLIANCE_URL,
} from "@/utils/constants/constants";
import alert_mixin from "@/utils/mixins/alert_mixin";
import entity_compliance_mixin from "@/utils/mixins/entity_compliance_mixin";
import axios from "axios";

export default {
  name: "EntityComplianceAnswers",
  mixins: [alert_mixin, entity_compliance_mixin],
  components: { FilePreviewComponent, ComplianceAnswerHistory },

  props: {
    entity_profile_id: {
      type: [String, Number],
      required: true,
    },
  },

  emits: ["data-loaded"],

  data: () => ({
    headers: get_headers(),
    overview_data: null,
    is_loading_overview: false,
    is_syncing: false,
    search_query: "",
    collapsed_sub_sections: {},
    expanded_rules: {},
    rule_details: {},
    loading_rule_details: {},
    show_file_preview: false,
    preview_file_url: "",
    preview_file_name: "",
    rule_evaluation_dates: {},
    // Inline editing / submission
    editing_question_id: null,
    edit_answer_value: "",
    edit_selected_choices: [],
    is_submitting_answer: false,
    // History Modal Trigger
    history_question: null,
    show_history_modal: false,
  }),

  computed: {
    has_data() {
      return this.overview_data !== null;
    },

    total_rules() {
      return Reflect.get(this.overview_data || {}, "total_rules") || 0;
    },

    total_questions() {
      return Reflect.get(this.overview_data || {}, "total_questions") || 0;
    },

    total_answered() {
      return Reflect.get(this.overview_data || {}, "total_answered") || 0;
    },

    total_unanswered() {
      return Reflect.get(this.overview_data || {}, "total_unanswered") || 0;
    },

    completion_percentage() {
      if (this.total_questions === 0) return 0;
      return Math.round((this.total_answered / this.total_questions) * 100);
    },

    progress_bar_style() {
      const progress_bar = {
        width: `${this.completion_percentage}%`,
        '--hue': this.progress_bar_hue
      };
      return progress_bar;
    },

    progress_bar_hue() {
      return 120 * (this.completion_percentage / 100);
    },


    rules_list() {
      return Reflect.get(this.overview_data || {}, "rules") || [];
    },

    filtered_rules() {
      const search_term = this.search_query.toLowerCase().trim();
      if (!search_term) return this.rules_list;

      return this.rules_list.filter((rule_item) => {
        const rule_name = (rule_item.name || "").toLowerCase();
        const rule_description = (rule_item.description || "").toLowerCase();
        return rule_name.includes(search_term) || rule_description.includes(search_term);
      });
    },

    entity_name() {
      return Reflect.get(this.overview_data || {}, "entity_name") || "Entity";
    },

    entity_type_name() {
      return Reflect.get(this.overview_data || {}, "entity_type_name") || "";
    },
  },

  watch: {
    entity_profile_id: {
      immediate: true,
      handler(new_id) {
        if (new_id) {
          this.load_compliance_overview();
        }
      },
    },
  },

  methods: {
    // ─── Data Loading ────────────────────────────────────────────

    async load_compliance_overview() {
      if (!this.entity_profile_id) {
        this.overview_data = null;
        return;
      }

      this.is_loading_overview = true;
      axios
        .get(GET_ENTITY_COMPLIANCE_OVERVIEW_URL, {
          headers: this.headers,
          params: { entity_profile_id: this.entity_profile_id },
        })
        .then((response) => {
          this.handle_overview_response(response);
        })
        .catch((error) => {
          this.handle_overview_error(error);
        })
        .finally(() => {
          this.is_loading_overview = false;
        });
    },

    handle_overview_response(response) {
      if (response.data.status === "success") {
        this.overview_data = response.data.data;
        const rules = this.overview_data.rules || [];
        rules.forEach(rule => {
          if (rule.id) this.load_rule_details(rule.id);
        });

        this.$emit("data-loaded", this.total_questions > 0);
      } else {
        this.overview_data = null;
        this.$emit("data-loaded", false);
      }
    },

    handle_overview_error(error) {
      this.show_error("Failed to load compliance data: " + (error.message || "Unknown error"));
      this.overview_data = null;
      this.$emit("data-loaded", false);
    },

    async load_rule_details(rule_id) {
      if (!this.entity_profile_id || !rule_id) return;

      Reflect.set(this.loading_rule_details, rule_id, true);
      axios
        .get(GET_RULE_QUESTIONS_ANSWERS_URL, {
          headers: this.headers,
          params: {
            compliance_rule_id: rule_id,
            entity_profile_id: this.entity_profile_id,
          },
        })
        .then((response) => {
          this.handle_rule_details_response(response, rule_id);
        })
        .catch((error) => {
          this.handle_rule_details_error(error, rule_id);
        })
        .finally(() => {
          Reflect.set(this.loading_rule_details, rule_id, false);
        });
    },

    handle_rule_details_response(response, rule_id) {
      if (Reflect.get(response.data, "status") === "success") {
        const response_data = Reflect.get(response.data, "data");
        Reflect.set(this.rule_details, rule_id, response_data);

        if (response_data.rule) {
          Reflect.set(this.rule_evaluation_dates, rule_id, {
            last_evaluation: response_data.rule.last_evaluation,
            next_evaluation: response_data.rule.next_evaluation
          });
        }
      }
    },

    handle_rule_details_error(error, rule_id) {
      this.show_error("Failed to load rule details: " + (error.message || "Unknown error"));
      Reflect.set(this.rule_details, rule_id, null);
    },

    // ─── Sync ────────────────────────────────────────────────────

    async sync_answers() {
      if (!this.entity_profile_id) return;

      this.is_syncing = true;
      axios
        .post(
          SYNC_APPLICATION_ANSWERS_TO_COMPLIANCE_URL,
          { entity_profile_id: this.entity_profile_id },
          { headers: this.headers }
        )
        .then((response) => {
          this.handle_sync_response(response);
        })
        .catch((error) => {
          this.handle_sync_error(error);
        })
        .finally(() => {
          this.is_syncing = false;
        });
    },

    handle_sync_response(response) {
      if (response.data.status === "success") {
        const synced_count = response.data.data.synced_count;
        this.show_success(`Synced ${synced_count} answers from application forms`);
        this.load_compliance_overview();
      } else {
        this.show_error(response.data.message || "Failed to sync");
      }
    },

    handle_sync_error(error) {
      this.show_error("Failed to sync answers: " + (error.message || "Unknown error"));
    },

    // ─── Rule / Sub-Section Expand & Collapse ────────────────────

    toggle_rule_expand(rule_id) {
      const is_expanded = Reflect.get(this.expanded_rules, rule_id) || false;
      const new_value = !is_expanded;
      Reflect.set(this.expanded_rules, rule_id, new_value);

      if (new_value && !Reflect.get(this.rule_details, rule_id)) {
        this.load_rule_details(rule_id);
      }
    },

    is_rule_expanded(rule_id) {
      return Reflect.get(this.expanded_rules, rule_id) || false;
    },

    is_rule_loading(rule_id) {
      return Reflect.get(this.loading_rule_details, rule_id) || false;
    },

    get_rule_details(rule_id) {
      return Reflect.get(this.rule_details, rule_id);
    },

    toggle_sub_section_collapse(sub_section_key) {
      const current = Reflect.get(this.collapsed_sub_sections, sub_section_key) || false;
      Reflect.set(this.collapsed_sub_sections, sub_section_key, !current);
    },

    is_sub_section_collapsed(sub_section_key) {
      return Reflect.get(this.collapsed_sub_sections, sub_section_key) || false;
    },

    get_all_sub_sections_for_rule(rule_id) {
      const rule_data = Reflect.get(this.rule_details, rule_id);
      if (!rule_data) return [];

      const main_sections = rule_data.main_sections || [];
      return main_sections.reduce((acc, ms) => acc.concat(ms.sub_sections || []), []);
    },

    // ─── Selection Helpers ──────────────────────────────────────

    is_choice_selected(choice, selected_answers) {
      return (selected_answers || []).includes(choice);
    },

    // ─── File Preview ────────────────────────────────────────────

    open_file_preview(presigned_url, file_name) {
      if (!presigned_url) {
        this.show_error("No file URL available");
        return;
      }
      this.preview_file_url = presigned_url;
      this.preview_file_name = file_name || "File";
      this.show_file_preview = true;
    },

    close_file_preview() {
      this.show_file_preview = false;
      this.preview_file_url = "";
      this.preview_file_name = "";
    },

    should_show_inline_input(question) {
      if (this.editing_question_id === question.id) return true;
      return !question.answer_source || question.answer_source === 'none';
    },

    start_editing(question) {
      this.editing_question_id = question.id;
      const existing_answer = question.answer || "";
      this.edit_answer_value = existing_answer;
      this.edit_selected_choices = [];
      this.edit_file = null;

      // Format date for <input type="date">
      if (question.question_type_raw === 'date' && existing_answer) {
        try {
          const date = new Date(existing_answer);
          if (!isNaN(date.getTime())) {
            this.edit_answer_value = date.toISOString().split('T')[0];
          }
        } catch (e) {
          console.warn("Failed to parse date for editing:", existing_answer);
        }
      }

      if (this.is_multiselect_type(question.question_type_raw) && existing_answer) {
        this.edit_selected_choices = Array.isArray(existing_answer)
          ? [...existing_answer]
          : String(existing_answer).split(',').map(s => s.trim()).filter(s => s);
      }
    },


    cancel_editing() {
      this.editing_question_id = null;
      this.edit_answer_value = "";
      this.edit_selected_choices = [];
      this.edit_file = null;
    },

    toggle_edit_choice(choice_text) {
      const idx = this.edit_selected_choices.indexOf(choice_text);
      if (idx > -1) {
        this.edit_selected_choices.splice(idx, 1);
      } else {
        this.edit_selected_choices.push(choice_text);
      }
    },

    handle_edit_file_change(event) {
      const file = event.target.files[0];
      if (file) this.edit_file = file;
    },

    remove_edit_file() {
      this.edit_file = null;
      const input = document.getElementById(`file-upload-${this.editing_question_id}`);
      if (input) input.value = "";
    },

    // ─── Submission Logic ────────────────────────────────────────

    async submit_answer(question) {
      this.is_submitting_answer = true;
      const formData = this._prepare_form_data(question);
      if (!formData) {
        this.is_submitting_answer = false;
        return;
      }

      axios
        .post(SUBMIT_COMPLIANCE_ANSWER_URL, formData, {
          headers: { ...this.headers, "Content-Type": "multipart/form-data" },
        })
        .then((response) => {
          if (response.data.status === "success") {
            this.show_success("Answer submitted successfully.");
            this.cancel_editing();
            this._refresh_after_submission();
          } else {
            this.show_error(response.data.message || "Failed to submit answer.");
          }
        })
        .catch(() => {
          this.show_error("An error occurred while submitting the answer.");
        })
        .finally(() => {
          this.is_submitting_answer = false;
        });
    },

    _prepare_form_data(question) {
      const formData = new FormData();
      formData.append("compliance_field_id", question.id);
      formData.append("entity_profile_id", this.entity_profile_id);

      let answer_val = "";
      if (this.is_file_type(question.question_type_raw)) {
        if (this.edit_file) {
          formData.append("file", this.edit_file);
          answer_val = this.edit_file.name;
        } else if (question.answer) {
          answer_val = question.answer;
        } else {
          this.show_error("Please select a file.");
          this.is_submitting_answer = false;
          return null;
        }
      } else if (this.is_multiselect_type(question.question_type_raw)) {
        answer_val = this.edit_selected_choices.join(",");
      } else {
        answer_val = this.edit_answer_value;
      }

      formData.append("answer", answer_val);
      return formData;
    },

    _refresh_after_submission() {
      Object.keys(this.expanded_rules).forEach(rule_id => {
        if (Reflect.get(this.expanded_rules, rule_id)) {
          this.load_rule_details(rule_id);
        }
      });
      this.load_compliance_overview();
    },

    // --- History Logic ---
    open_history_modal(question) {
      this.history_question = question;
      this.show_history_modal = true;
    },
  },
};
